/**
 * productIcons - Provides React icon elements for products and resource types.
 *
 * Two levels of icon resolution:
 * 1. Resource-type icon: e.g. page icon for Confluence Page
 * 2. Product logo: e.g. Confluence logo (fallback)
 *
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

import { css, jsx } from '@atlaskit/css';
import {
	confluenceIcon,
	jiraIcon,
	bitbucketIcon,
	trelloIcon,
	opsgenieIcon,
	statuspageIcon,
	moreAtlassianAppsIcon,
	loomIcon,
	guardIcon,
	studioIcon,
	assetsIcon,
	focusIcon,
	teamsIcon,
	homeIcon,
	compassIcon,
	rovoIcon,
	goalsIcon,
	projectsIcon,
	adminIcon,
	analyticsIcon,
	jiraProductDiscoveryIcon,
	jiraServiceManagementIcon,
} from '@atlaskit/logo/raw-icons';
import { token } from '@atlaskit/tokens';

// ── Asset logos for third-party (and gap-filling first-party) products ──
// Imports below are URL imports of svg files in `src/assets/logos/` that
// the bundler will rewrite to a real asset URL at build time.

import adobexdAssetLogo from '../../../../../../../assets/logos/adobexd.svg';
import airtableAssetLogo from '../../../../../../../assets/logos/airtable.svg';
import amplitudeAssetLogo from '../../../../../../../assets/logos/amplitude.svg';
import asanaAssetLogo from '../../../../../../../assets/logos/asana.svg';
import azureDevopsAssetLogo from '../../../../../../../assets/logos/azure_devops.svg';
import boxAssetLogo from '../../../../../../../assets/logos/box.svg';
import clickupAssetLogo from '../../../../../../../assets/logos/clickup.svg';
import docusignAssetLogo from '../../../../../../../assets/logos/docusign.svg';
import dovetailAssetLogo from '../../../../../../../assets/logos/dovetail.svg';
import dropboxAssetLogo from '../../../../../../../assets/logos/dropbox.svg';
import figmaAssetLogo from '../../../../../../../assets/logos/figma.svg';
import githubAssetLogo from '../../../../../../../assets/logos/github.svg';
import gitlabAssetLogo from '../../../../../../../assets/logos/gitlab.svg';
import googleCalendarAssetLogo from '../../../../../../../assets/logos/google_calendar.svg';
import googleDriveAssetLogo from '../../../../../../../assets/logos/google_drive.svg';
import hubspotAssetLogo from '../../../../../../../assets/logos/hubspot.svg';
import launchdarklyAssetLogo from '../../../../../../../assets/logos/launchdarkly.svg';
import loomAssetLogo from '../../../../../../../assets/logos/loom.svg';
import lucidchartAssetLogo from '../../../../../../../assets/logos/lucidchart.svg';
import microsoftSharepointAssetLogo from '../../../../../../../assets/logos/microsoft_sharepoint.svg';
import microsoftTeamsAssetLogo from '../../../../../../../assets/logos/microsoft_teams.svg';
import miroAssetLogo from '../../../../../../../assets/logos/miro.svg';
import mondayAssetLogo from '../../../../../../../assets/logos/monday.svg';
import muralAssetLogo from '../../../../../../../assets/logos/mural.svg';
import notionAssetLogo from '../../../../../../../assets/logos/notion.svg';
import onedriveAssetLogo from '../../../../../../../assets/logos/onedrive.svg';
import outlookCalendarAssetLogo from '../../../../../../../assets/logos/outlook-calendar.svg';
import pagerdutyAssetLogo from '../../../../../../../assets/logos/pagerduty.svg';
import pipedriveAssetLogo from '../../../../../../../assets/logos/pipedrive.svg';
import powerBiAssetLogo from '../../../../../../../assets/logos/power_bi.svg';
import salesforceAssetLogo from '../../../../../../../assets/logos/salesforce.svg';
import sentryAssetLogo from '../../../../../../../assets/logos/sentry.svg';
import slackAssetLogo from '../../../../../../../assets/logos/slack.svg';
import smartsheetAssetLogo from '../../../../../../../assets/logos/smartsheet.svg';
import stripeAssetLogo from '../../../../../../../assets/logos/stripe.svg';
import todoistAssetLogo from '../../../../../../../assets/logos/todoist.svg';
import webexAssetLogo from '../../../../../../../assets/logos/webex.svg';
import workdayAssetLogo from '../../../../../../../assets/logos/workday.svg';
import zeplinAssetLogo from '../../../../../../../assets/logos/zeplin.svg';
import { coreIconImporters, iconLabImporters } from '../utils/iconImporters';

// ── Product logos from @atlaskit/logo/raw-icons (data URIs) ─────────────

import {
	resolveProductByKey,
	resolveProductForNode,
	getProductKey,
	getResourceType,
	getProductForThirdPartyId,
} from './productRegistry';

/**
 * The synthetic resource owner used by the third-party graph bridge.
 * Bridged entities have ARIs of the form `ari:cloud:graph::<resourceType>/...`,
 * so `getProductKey(node.id)` returns this literal for them.
 */
const GRAPH_BRIDGE_PRODUCT_KEY = 'graph';

// ============================================================================
// Resource-type icon dynamic loading
// ----------------------------------------------------------------------------
// Instead of a hardcoded allowlist, icons are resolved at runtime using the
// generated `coreIconImporters` / `iconLabImporters` maps from the temp-logo
// page. The importers are concrete `() => import('@atlaskit/icon/core/<name>')`
// functions, so the bundler (Atlaspack/Parcel) keeps each icon module in the
// build but defers fetching until first use.
// ============================================================================

type IconComponent = React.ComponentType<any>;

const loadedIcons = new Map<string, IconComponent>();
const loadingIcons = new Map<string, Promise<IconComponent | null>>();
const warnedUnsupportedRegistryIcons = new Set<string>();

function warnUnsupportedRegistryIcon(importPath: string): void {
	if (warnedUnsupportedRegistryIcons.has(importPath)) {
		return;
	}

	warnedUnsupportedRegistryIcons.add(importPath);
	console.warn(
		`[simple-home] Product registry icon "${importPath}" is configured but not available in the icon importers.`,
	);
}

/**
 * Given a full package importPath (e.g. `@atlaskit/icon/core/page` or
 * `@atlaskit/icon-lab/core/loom`), resolve the matching importer function
 * from the generated importer maps.
 *
 * The importer maps use **short icon names** as keys (e.g. `'page'`, `'loom'`),
 * so we strip the package prefix before looking up.
 */
function getIconImporter(importPath: string): (() => Promise<{ default: any }>) | null {
	// @atlaskit/icon/core/<name>
	const coreMatch = importPath.match(/^@atlaskit\/icon\/core\/(.+)$/);
	if (coreMatch) {
		return coreIconImporters[coreMatch[1]] ?? null;
	}
	// @atlaskit/icon-lab/core/<name>
	const labMatch = importPath.match(/^@atlaskit\/icon-lab\/core\/(.+)$/);
	if (labMatch) {
		return iconLabImporters[labMatch[1]] ?? null;
	}
	return null;
}

/**
 * Synchronously read the cached icon component for an importPath. Returns
 * null if the icon hasn't been loaded yet (or doesn't exist in the importer
 * maps). Use together with `loadProductIcon` to drive a re-render once
 * resolution completes.
 */
export function getLoadedProductIcon(importPath: string): IconComponent | null {
	return loadedIcons.get(importPath) ?? null;
}

/**
 * Kick off (or join an in-flight) load for an icon component, caching the
 * resolved module's `default` export. Returns null if the importPath is not
 * present in the importer maps.
 */
export function loadProductIcon(importPath: string): Promise<IconComponent | null> {
	const cached = loadedIcons.get(importPath);
	if (cached) {
		return Promise.resolve(cached);
	}

	const inflight = loadingIcons.get(importPath);
	if (inflight) {
		return inflight;
	}

	const importer = getIconImporter(importPath);
	if (!importer) {
		warnUnsupportedRegistryIcon(importPath);
		return Promise.resolve(null);
	}

	const promise = importer()
		.then((mod) => {
			const Comp = (mod?.default ?? null) as IconComponent | null;
			if (Comp) {
				loadedIcons.set(importPath, Comp);
			}
			loadingIcons.delete(importPath);
			return Comp;
		})
		.catch((err) => {
			loadingIcons.delete(importPath);
			console.warn(`[simple-home] Failed to load icon "${importPath}":`, err);
			return null;
		});

	loadingIcons.set(importPath, promise);
	return promise;
}

// ============================================================================
// Product logo map (resource owner key → data URI)
// ============================================================================

const PRODUCT_LOGO_URLS: Record<string, string> = {
	confluence: confluenceIcon,
	jira: jiraIcon,
	bitbucket: bitbucketIcon,
	trello: trelloIcon,
	opsgenie: opsgenieIcon,
	statuspage: statuspageIcon,
	loom: loomIcon,
	compass: compassIcon,
	townsquare: homeIcon,
	home: homeIcon,
	goal: goalsIcon,
	project: projectsIcon,
	rovo: rovoIcon,
	'convo-ai': rovoIcon,
	teams: teamsIcon,
	passionfruit: guardIcon,
	pure: guardIcon,
	studio: studioIcon,
	cmdb: assetsIcon,
	'assets-vertical': assetsIcon,
	mercury: focusIcon,
	avocado: adminIcon,
	analytics: analyticsIcon,
	'jira-product-discovery': jiraProductDiscoveryIcon,
	'jira-servicedesk': jiraServiceManagementIcon,
	'jira-software': jiraIcon,
	atlas: moreAtlassianAppsIcon,
	identity: moreAtlassianAppsIcon,
	atlassian: moreAtlassianAppsIcon,
};

// ============================================================================
// Asset logo map (svgKey or resource owner key → asset URL)
// ----------------------------------------------------------------------------
// The product registry stamps each third-party (and gap-filling first-party)
// product with `logo: { source: 'asset', svgKey: '<key>', importPath: '...' }`.
// We normalise the lookup against this map keyed by the registry's `svgKey`.
//
// Additional aliases keyed by the product's resource owner (e.g. `slack.slack`,
// `jira-align`) and bare product slug (`slack`, `github`) are added so the
// helpers below can resolve a logo from any of:
//   - `product.logo.svgKey`
//   - `product.resourceOwner`
//   - bare product slug derived from a `thirdPartyId`
// without callers having to know which key shape is in play.
// ============================================================================
const ASSET_LOGO_URLS: Record<string, string> = {
	// svgKey → asset (matches `logo.svgKey` values in product-registry.json)
	adobexd: adobexdAssetLogo,
	xd: adobexdAssetLogo,
	airtable: airtableAssetLogo,
	amplitude: amplitudeAssetLogo,
	asana: asanaAssetLogo,
	azure_azure_devops: azureDevopsAssetLogo,
	azure_devops: azureDevopsAssetLogo,
	box: boxAssetLogo,
	clickup: clickupAssetLogo,
	docusign: docusignAssetLogo,
	dovetail: dovetailAssetLogo,
	dropbox: dropboxAssetLogo,
	figma: figmaAssetLogo,
	github: githubAssetLogo,
	gitlab: gitlabAssetLogo,
	google_calendar: googleCalendarAssetLogo,
	'google-calendar': googleCalendarAssetLogo,
	google_drive: googleDriveAssetLogo,
	'google-drive': googleDriveAssetLogo,
	hubspot: hubspotAssetLogo,
	launchdarkly: launchdarklyAssetLogo,
	loom: loomAssetLogo,
	lucidchart: lucidchartAssetLogo,
	lucid: lucidchartAssetLogo,
	microsoft_sharepoint: microsoftSharepointAssetLogo,
	microsoft_teams: microsoftTeamsAssetLogo,
	microsoft_onedrive: onedriveAssetLogo,
	miro: miroAssetLogo,
	monday: mondayAssetLogo,
	mural: muralAssetLogo,
	notion: notionAssetLogo,
	onedrive: onedriveAssetLogo,
	'outlook-calendar': outlookCalendarAssetLogo,
	outlook_calendar: outlookCalendarAssetLogo,
	pagerduty: pagerdutyAssetLogo,
	pipedrive: pipedriveAssetLogo,
	azure_power_bi_embedded: powerBiAssetLogo,
	power_bi: powerBiAssetLogo,
	'power-bi': powerBiAssetLogo,
	salesforce: salesforceAssetLogo,
	sentry: sentryAssetLogo,
	slack: slackAssetLogo,
	smartsheet: smartsheetAssetLogo,
	stripe: stripeAssetLogo,
	todoist: todoistAssetLogo,
	webex: webexAssetLogo,
	workday: workdayAssetLogo,
	zeplin: zeplinAssetLogo,

	// Aliases keyed by resource owner (`<vendor>.<product>` form) so a
	// `product.resourceOwner` lookup resolves directly.
	'adobe.adobexd': adobexdAssetLogo,
	'airtable.airtable': airtableAssetLogo,
	'amplitude.amplitude': amplitudeAssetLogo,
	'asana.asana': asanaAssetLogo,
	'azure-devops.azure-devops': azureDevopsAssetLogo,
	'box.box': boxAssetLogo,
	'cisco.webex': webexAssetLogo,
	'clickup.clickup': clickupAssetLogo,
	'docusign.docusign': docusignAssetLogo,
	'dovetail.dovetail': dovetailAssetLogo,
	'dropbox.dropbox': dropboxAssetLogo,
	'figma.figma': figmaAssetLogo,
	'github.github': githubAssetLogo,
	'gitlab.gitlab': gitlabAssetLogo,
	'google.google-calendar': googleCalendarAssetLogo,
	'google.google-drive': googleDriveAssetLogo,
	'google.google-drive-rsl': googleDriveAssetLogo,
	'google.google-drive-lite': googleDriveAssetLogo,
	'hubspot.hubspot': hubspotAssetLogo,
	'launchdarkly.launchdarkly': launchdarklyAssetLogo,
	'loom.loom': loomAssetLogo,
	'lucid.lucidchart': lucidchartAssetLogo,
	'microsoft.onedrive': onedriveAssetLogo,
	'microsoft.power-bi': powerBiAssetLogo,
	'microsoft.sharepoint': microsoftSharepointAssetLogo,
	'microsoft.teams': microsoftTeamsAssetLogo,
	'miro.miro': miroAssetLogo,
	'monday.monday': mondayAssetLogo,
	'mural.mural': muralAssetLogo,
	'notion.notion': notionAssetLogo,
	'pagerduty.pagerduty': pagerdutyAssetLogo,
	'pipedrive.pipedrive': pipedriveAssetLogo,
	'salesforce.salesforce': salesforceAssetLogo,
	'sentry.sentry': sentryAssetLogo,
	'slack.slack': slackAssetLogo,
	'smartsheet.smartsheet': smartsheetAssetLogo,
	'stripe.stripe': stripeAssetLogo,
	'todoist.todoist': todoistAssetLogo,
	'workday.workday': workdayAssetLogo,
	'zeplin.zeplin': zeplinAssetLogo,
};

/**
 * Resolve an asset logo URL for any of: a registry `svgKey`, a product
 * `resourceOwner`, or a bare product slug. Returns undefined if no logo
 * is registered.
 */
function resolveAssetLogoUrl(key: string | null | undefined): string | undefined {
	if (!key) return undefined;
	return ASSET_LOGO_URLS[key];
}

/**
 * Pull the connector hint encoded on a graph-bridged node so we can resolve
 * its third-party product. Inspects `productDisplayName` (set by
 * `cypherQueryService.hydrateEntityValue` when `thirdPartyId` was present),
 * then `thirdPartyId`, then `thirdPartyAri` as a final fallback for nodes
 * surfaced through paths that don't populate `thirdPartyId`.
 */
function inferThirdPartyProduct(node: {
	id: string;
	integration?: string;
	thirdPartyId?: string;
}): { displayName: string; resourceOwner: string; svgKey?: string } | null {
	// Only graph-bridged ARIs need this fallback; regular product ARIs
	// resolve via `resolveProductForNode`.
	const ariKey = getProductKey(node.id);
	if (ariKey !== 'graph') return null;

	// `integration` is populated by display-derivation paths from
	// `properties.productDisplayName` and is the most reliable signal at
	// the point of icon rendering.
	const integration = node.integration?.trim();
	if (integration) {
		const product = getProductForThirdPartyId(integration);
		if (product) {
			return {
				displayName: product.displayName,
				resourceOwner: product.resourceOwner,
				svgKey: product.logo?.svgKey,
			};
		}
		// Last-ditch: use the integration label as a slug.
		return { displayName: integration, resourceOwner: integration.toLowerCase() };
	}

	// Final fallback: many graph-bridged External entities (calendar
	// events, Google Docs, Slack messages, Loom videos, Figma links, …)
	// arrive WITHOUT `properties.productDisplayName` — i.e. with no
	// `integration` hint by the time we get here. They DO carry the
	// connector identity inside `properties.thirdPartyId` (e.g.
	// `ari:third-party:google.google-calendar::…`). Plumb that ARI
	// through `getProductForThirdPartyId` which already understands
	// both `ari:third-party:<vendor>.<product>::…` and
	// `ari:cloud:<product>::…` shapes and lands on the right product.
	const tpId = node.thirdPartyId?.trim();
	if (tpId) {
		const product = getProductForThirdPartyId(tpId);
		if (product) {
			return {
				displayName: product.displayName,
				resourceOwner: product.resourceOwner,
				svgKey: product.logo?.svgKey,
			};
		}
	}
	return null;
}

// ============================================================================
// Styles
// ============================================================================

const productLogoImgStyles = css({
	width: '20px',
	height: '20px',
	borderRadius: token('radius.small'),
});

// ============================================================================
// Public API
// ============================================================================

/**
 * Get a React element rendering the **product logo** for a discovered node.
 *
 * Resource-type icons are loaded asynchronously by consumers via
 * `getProductIconImportPathForNode` + `useProductIcon`, so this helper only
 * resolves the synchronous logo fallback.
 *
 * Returns a JSX element or null if no logo is available.
 */
export function getProductIconForNode(node: {
	id: string;
	integration?: string;
	thirdPartyId?: string;
}): JSX.Element | null {
	const url = getProductLogoUrl(node);
	if (url) {
		return <img src={url} alt="" css={productLogoImgStyles} />;
	}
	return null;
}

/**
 * Get the resource-type icon **importPath** (e.g. `@atlaskit/icon/core/page`)
 * for a node, looked up from the product registry. Returns null if the node's
 * resource type has no registered icon importPath.
 *
 * Bridged-entity rule: for entities surfaced through the third-party graph
 * bridge (`ari:cloud:graph::<resourceType>/...`), the **secondary icon**
 * (resource-type icon) is intentionally resolved against the `graph`
 * product registry rather than the originating third-party product. The
 * primary graph ARI carries the authoritative resource type for what is
 * being displayed (e.g. `pull-request`, `video`, `repository`), and the
 * `graph` registry has full coverage of these resource types with stable
 * Atlassian Design System icons. The third-party logo (Loom, GitHub, …)
 * is still surfaced separately via `getProductLogoUrl` /
 * `getProductIconForNode`.
 *
 * If the `graph` registry has no importPath for the bridged resource type,
 * we fall back to the third-party product lookup so unmapped types don't
 * regress to a missing icon.
 *
 * Consumers pair this with the `useProductIcon` hook (or
 * `loadProductIcon` / `getLoadedProductIcon`) to lazily resolve and render
 * the actual icon component.
 */
export function getProductIconImportPathForNode(node: {
	id: string;
	integration?: string;
	thirdPartyId?: string;
}): string | null {
	const resourceType = getResourceType(node.id);
	if (!resourceType) return null;

	// Bridged entities: prefer the `graph` registry for the secondary
	// icon. The graph ARI's resource type is the source of truth for
	// "what kind of thing am I looking at", regardless of which 3P
	// connector originated it.
	if (getProductKey(node.id) === GRAPH_BRIDGE_PRODUCT_KEY) {
		const graphProduct = resolveProductByKey(GRAPH_BRIDGE_PRODUCT_KEY);
		const graphImportPath = graphProduct?.resourceTypes[resourceType]?.icon?.importPath;
		if (graphImportPath) return graphImportPath;
		// Fall through to the third-party product lookup below if the
		// graph registry doesn't cover this resource type — avoids
		// regressing to a missing icon for any not-yet-registered type.
	}

	const product = resolveProductForNode(node);
	if (!product) return null;

	const rtInfo = product.resourceTypes[resourceType];
	return rtInfo?.icon?.importPath ?? null;
}

/**
 * Get just the product logo URL (data URI) for fallback rendering.
 * Used when the node component needs to render an <img> directly.
 */
export function getProductLogoUrl(node: {
	id: string;
	integration?: string;
	thirdPartyId?: string;
}): string | undefined {
	const product = resolveProductForNode(node);
	if (product) {
		// Prefer first-party @atlaskit/logo data URI when registered.
		const builtInUrl = PRODUCT_LOGO_URLS[product.resourceOwner];
		if (builtInUrl) return builtInUrl;
		// Then try the asset map by resource owner and (if present) svgKey.
		const assetUrl =
			resolveAssetLogoUrl(product.logo?.svgKey) ?? resolveAssetLogoUrl(product.resourceOwner);
		if (assetUrl) return assetUrl;
	}

	const ariKey = getProductKey(node.id);
	if (ariKey) {
		if (PRODUCT_LOGO_URLS[ariKey]) return PRODUCT_LOGO_URLS[ariKey];
		const assetByAri = resolveAssetLogoUrl(ariKey);
		if (assetByAri) return assetByAri;
	}

	if (node.integration) {
		const intLower = node.integration.toLowerCase();
		if (PRODUCT_LOGO_URLS[intLower]) return PRODUCT_LOGO_URLS[intLower];
		const assetByInt = resolveAssetLogoUrl(intLower);
		if (assetByInt) return assetByInt;
	}

	// Final fallback for graph-bridged nodes (`ari:cloud:graph::*`): use the
	// connector hint from `properties.productDisplayName` (surfaced via
	// `node.integration` by the display-derivation layer) to resolve the
	// originating third-party product, then render its asset logo. This
	// covers transitively-discovered bridged nodes where neither the
	// product registry nor the AGG-stamped `thirdPartyId` is present on
	// the node directly.
	const thirdParty = inferThirdPartyProduct(node);
	if (thirdParty) {
		const assetByTp =
			resolveAssetLogoUrl(thirdParty.svgKey) ??
			resolveAssetLogoUrl(thirdParty.resourceOwner) ??
			PRODUCT_LOGO_URLS[thirdParty.resourceOwner];
		if (assetByTp) return assetByTp;
	}

	return undefined;
}
